import { useAuth0 } from "@auth0/auth0-react";
import DoneRoundedIcon from "@mui/icons-material/DoneRounded";
import { Box, Divider } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import useSmartAndRegularAssets from "../../../../api/hooks/useSmartAndRegularAssets";
import { scenarioSetKeys, stepKeys } from "../../../../api/queryKeys";
import SwitchButton from "../../../../components/filters/SwitchButton";
import LabelComponentProp from "../../../../components/LabelComponentProp";
import { CustomerFeatureFlagsContext } from "../../../../contexts/CustomerFeatureFlagsContext";
import { EnvFeatureFlagContext } from "../../../../contexts/EnvFeaturesContext";
import {
  ScenariosModalState,
  ScenariosModalStateLogic,
} from "../../../../utils/constants";
import { formatNumberCompact } from "../../../../utils/formatters";
import PercentageLabel from "../../frameworks/components/PercentageLabel";
import ScenarioSetFullFlow from "./ScenarioSetFullFlow";
import ScenarioSetNavigationButtons from "./ScenarioSetNavigationButtons";

export default function ScenarioSetModal({
  steps,
  scenariosSetsRowsCard,
  stepsFetchIsLoading,
  allScenarioSets,
  isLoadingScenarioSets,
  setSelectedScenarioSet,
  selectedScenarioSet,
}) {
  const [open, setOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [clickUrl, setClickUrl] = useState(false);
  const location = useLocation();
  const { user } = useAuth0();
  const queryClient = useQueryClient();
  const { detectionsActive } = useContext(CustomerFeatureFlagsContext);
  const [scenarioSetDetectedSteps, setScenarioSetDetectedSteps] = useState([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);

  const { regularAndActiveSmartAsset: assets } = useSmartAndRegularAssets();
  const { scenariosDemoMode, scenariosSingleMode, businessValueMode } =
    useContext(EnvFeatureFlagContext);

  const selectedAsset = assets?.find(
    (asset) => asset.asset_id === selectedScenarioSet?.asset_id
  );

  const NUMBER_OF_SCENARIOS_PER_PAGE = 5;
  const hasFewScenarios = useMemo(
    () => selectedScenarioSet?.scenario_count < NUMBER_OF_SCENARIOS_PER_PAGE,
    [selectedScenarioSet]
  );

  const [logic, setLogic] = useState(() => {
    // Initialize with Unified Paths if demo mode, not single mode, or few scenarios
    if (scenariosDemoMode || !scenariosSingleMode || hasFewScenarios) {
      return ScenariosModalStateLogic.UnifiedPaths;
    }
    return ScenariosModalStateLogic.SinglePaths;
  });

  const handleLogicChange = (newLogic) => {
    setLogic(newLogic);
  };
  const colors = useColors();

  const { trackEvent, setScenarioSetModalWasOpened, getAllDetectedStepIDs } =
    useAPI();

  const mutateOpenModal = useMutation({
    mutationFn: setScenarioSetModalWasOpened,
    onSuccess: () => {},
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setWindowHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (scenariosDemoMode || !scenariosSingleMode) {
      // Force UnifiedPaths when in demo mode or not in single mode
      setLogic(ScenariosModalStateLogic.UnifiedPaths);
    } else if (hasFewScenarios) {
      // Force UnifiedPaths when there are few scenarios
      setLogic(ScenariosModalStateLogic.UnifiedPaths);
    }
  }, [
    scenariosDemoMode,
    scenariosSingleMode,
    hasFewScenarios,
    selectedScenarioSet?.locations_and_phases_and_asset,
  ]);

  // When opening modal for a new scenario set
  useEffect(() => {
    if (selectedScenarioSet && open) {
      // Set initial view based on features and scenario count
      if (scenariosDemoMode || !scenariosSingleMode || hasFewScenarios) {
        setLogic(ScenariosModalStateLogic.UnifiedPaths);
      } else {
        setLogic(ScenariosModalStateLogic.SinglePaths);
      }
    }
  }, [open, selectedScenarioSet?.locations_and_phases_and_asset]);

  const headerScenariosSwitch = (logic, currentId) => {
    const scenarioSetIndex =
      scenariosSetsRowsCard?.findIndex(
        (scenarioSet) => scenarioSet.id === currentId
      ) + 1;
    switch (logic) {
      case "UnifiedPaths":
        return `Viewing all connected paths for Scenario ${scenarioSetIndex}`;
      case "SinglePaths":
        return `Viewing individual paths for Scenario ${scenarioSetIndex}`;
      default:
        return "";
    }
  };

  const TooltipScenariosSwitch = (logic, currentId) => {
    const scenarioSetIndex =
      scenariosSetsRowsCard?.findIndex(
        (scenarioSet) => scenarioSet.id === currentId
      ) + 1;
    return {
      UnifiedPaths: `View all connected attack paths to identify choke points.`,
      SinglePaths: `Focus on individual attack paths to analyze each in detail.`,
    };
  };

  const disabledArrows = (direction) => {
    const currentId = selectedScenarioSet.locations_and_phases_and_asset;
    const index = scenariosSetsRowsCard?.findIndex(
      (scenarioSet) => scenarioSet.id === currentId
    );
    const newIndex =
      direction === "next"
        ? index + 1
        : direction === "previous"
        ? index - 1
        : index;
    if (newIndex < 0 && direction === "previous") {
      return true;
    } else if (
      newIndex > scenariosSetsRowsCard?.length - 1 &&
      direction === "next"
    ) {
      return true;
    } else return false;
  };

  const moveScenarioSet = (direction) => {
    trackEvent("User clicked on " + direction + " scenario set");
    const currentId = selectedScenarioSet.locations_and_phases_and_asset;
    const index = scenariosSetsRowsCard?.findIndex(
      (scenarioSet) => scenarioSet.id === currentId
    );

    const newIndex =
      direction === "next"
        ? index + 1
        : direction === "previous"
        ? index - 1
        : index;

    if (newIndex >= 0 && newIndex < scenariosSetsRowsCard?.length) {
      const newId = scenariosSetsRowsCard[newIndex]?.id;
      const params = new URLSearchParams(location.search);
      const scenarioSetId = encodeURIComponent(newId);
      params.set("selected_scenario_set", scenarioSetId);
      setSearchParams(params.toString());
    }
  };

  useEffect(() => {
    if (isLoadingScenarioSets) {
      return;
    }

    const localSearchParams = new URLSearchParams(location.search);
    const selectedScenarioSetId = decodeURIComponent(
      localSearchParams.get("selected_scenario_set")
    );
    const selectedScenarioSetByUrl = allScenarioSets?.find(
      (scenarioSet) =>
        scenarioSet.locations_and_phases_and_asset === selectedScenarioSetId
    );
    if (selectedScenarioSetByUrl) {
      setSelectedScenarioSet(selectedScenarioSetByUrl);
      setOpen(true);
      const alreadyOpened =
        selectedScenarioSetByUrl.opened_by_user?.includes(user.email) || false;
      if (!alreadyOpened) {
        mutateOpenModal.mutate({
          scenarioSetId:
            selectedScenarioSetByUrl.locations_and_phases_and_asset,
        });
      }
      // NOTE: this track message is monitored by Mixpanel to collect data on user engagement - DO NOT CHANGE IT
      trackEvent(
        "User opened modal for ScenarioSet id " + selectedScenarioSetId
      );
    }
  }, [location, isLoadingScenarioSets]);

  const handleClose = () => {
    const params = new URLSearchParams(location.search);
    params.delete("selected_scenario_set");
    params.delete("showActiveDetection");
    setSearchParams(params);
    setOpen(false);
    setClickUrl(false);
    queryClient.invalidateQueries({ queryKey: scenarioSetKeys.all });
    trackEvent("ScenarioSet modal closed");
  };

  const UrlCopyTooltip = () => {
    return (
      <Box display={"flex"} alignItems={"center"} gap={1}>
        {clickUrl ? (
          <DoneRoundedIcon sx={{ color: colors.success_main, width: "18px" }} />
        ) : null}
        <Typography variant="caption" sx={{ color: colors.common_black }}>
          {clickUrl ? "Copied to Clipboard" : "Copy URL"}
        </Typography>
      </Box>
    );
  };

  const ScenarioSetNumber = ({ currentId }) => {
    const index = scenariosSetsRowsCard?.findIndex(
      (scenarioSet) => scenarioSet.id === currentId
    );

    const { data: detectedStepIDs } = useQuery({
      queryKey: stepKeys.detectedSteps(),
      enabled: detectionsActive !== false,
      queryFn: async () => {
        try {
          const response = await getAllDetectedStepIDs();
          return response.data;
        } catch (err) {
          return [];
        }
      },
    });

    useEffect(() => {
      if (detectedStepIDs && steps) {
        const stepsArray = Object.values(steps);
        setScenarioSetDetectedSteps((prev) => {
          const newDetected = stepsArray.filter((step) =>
            detectedStepIDs.includes(step.step_id)
          );
          if (JSON.stringify(prev) !== JSON.stringify(newDetected)) {
            return newDetected;
          }
          return prev;
        });
      }
    }, [detectedStepIDs, steps]);

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: "8px",
          alignItems: "center",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            fontSize: "14px",
          }}
        >
          {index + 1} {"of " + scenariosSetsRowsCard?.length}
        </Typography>
      </Box>
    );
  };

  const sideDialogButtonStyle = {
    cursor: "pointer",
    minWidth: "10px",
    backgroundColor: colors.common_white,
    borderRadius: "50%",
    boxShadow: "0px 0px 4px 0px rgba(0,0,0,0.25)",
    color: colors.action_active,
    "&:hover": {
      backgroundColor: colors.common_white,
    },
    "&.Mui-disabled": {
      backgroundColor: colors.common_white,
      color: colors.text_disabled,
    },
  };

  return (
    <>
      {selectedScenarioSet ? (
        <Dialog
          open={open}
          onClose={handleClose}
          maxWidth={false}
          style={{ zIndex: 800, overflow: "hidden" }}
          PaperProps={{
            style: {
              width: `${windowWidth * 0.9}px`,
              height: `${windowHeight * 0.95 + 10}px`,
              overflow: "hidden",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              flex: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "16px 16px 0 16px ",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                width: "100%",
                overflow: "hidden",
              }}
            >
              {/* Attack Scenario number */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: colors.primary_main,
                  }}
                >
                  Attack Scenario
                </Typography>
                {scenariosSetsRowsCard && (
                  <ScenarioSetNumber
                    currentId={
                      selectedScenarioSet.locations_and_phases_and_asset
                    }
                  />
                )}
              </Box>
              {/* Left Section */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Box display="flex" alignItems="center" gap={2}>
                  {/* Mitigated Paths Block */}
                  <LabelComponentProp
                    label="Mitigated Paths"
                    textTypography="caption"
                    alignItems="flex-start"
                    sx={{
                      minHeight: "30px",
                      gap: "8px",
                      color: colors.text_secondary,
                    }}
                  >
                    <Box
                      display="flex"
                      flexDirection="row"
                      gap={"12px"}
                      alignItems="center"
                    >
                      <PercentageLabel
                        percentage={selectedScenarioSet.coverage}
                        sx={{ borderRadius: "10px" }}
                      />
                      <Box display="flex" flexDirection="row" gap={0.5}>
                        <Typography
                          variant="subtitle2"
                          color={colors.text_primary}
                        >
                          {selectedScenarioSet?.mitigated_scenario_count +
                            "/" +
                            (selectedScenarioSet?.mitigated_scenario_count +
                              selectedScenarioSet?.unmitigated_scenario_count)}
                        </Typography>
                      </Box>
                    </Box>
                  </LabelComponentProp>
                  {businessValueMode && (
                    <>
                      <Divider orientation="vertical" flexItem />
                      <LabelComponentProp
                        label="Value at Risk"
                        textTypography="caption"
                        alignItems="flex-start"
                        sx={{
                          minHeight: "30px",
                          gap: "8px",
                        }}
                      >
                        <Box
                          display="flex"
                          flexDirection="row"
                          gap={1}
                          alignItems="center"
                        >
                          <Typography
                            variant="subtitle2"
                            color={colors.text_primary}
                          >
                            $
                            {formatNumberCompact(
                              selectedScenarioSet?.value_at_risk
                            )}
                          </Typography>
                        </Box>
                      </LabelComponentProp>
                    </>
                  )}

                  {/* Divider and Detections Block */}
                  {detectionsActive && scenarioSetDetectedSteps.length > 0 && (
                    <>
                      <Divider orientation="vertical" flexItem />
                      {/* Detections Block */}
                      <LabelComponentProp
                        label="Detected Steps"
                        textTypography="caption"
                        alignItems="flex-start"
                        sx={{
                          minHeight: "30px",
                          gap: "8px",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          color={colors.text_primary}
                        >
                          {scenarioSetDetectedSteps.length}
                        </Typography>
                      </LabelComponentProp>
                    </>
                  )}
                </Box>
                {scenariosSingleMode && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      width: "280px",
                    }}
                  >
                    <SwitchButton
                      selectedLogic={logic}
                      onChange={handleLogicChange}
                      logicsAndLabels={ScenariosModalState}
                      TooltipSwitch={TooltipScenariosSwitch(
                        logic,
                        selectedScenarioSet.locations_and_phases_and_asset
                      )}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.text_secondary,
                        padding: "4px",
                      }}
                    >
                      {headerScenariosSwitch(
                        logic,
                        selectedScenarioSet.locations_and_phases_and_asset
                      )}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ overflow: "hidden", padding: "16px" }}>
            <ScenarioSetFullFlow
              width={windowWidth * 0.85} // Subtract the DialogContent's padding from the width
              height={windowHeight * 0.65 - 10} // Subtract the DialogContent's padding from the height
              scenarioSet={selectedScenarioSet}
              fetchedSteps={steps}
              stepsFetchIsLoading={stepsFetchIsLoading}
              selectedAsset={selectedAsset}
              // Add logic to key to force re-render when logic changes
              key={`${selectedScenarioSet.locations_and_phases_and_asset}-${logic}`}
              logic={logic}
            />
          </DialogContent>
        </Dialog>
      ) : null}
      {open && (
        <ScenarioSetNavigationButtons
          handleClose={handleClose}
          moveScenarioSet={moveScenarioSet}
          disabledArrows={disabledArrows}
          setClickUrl={setClickUrl}
          trackEvent={trackEvent}
          sideDialogButtonStyle={sideDialogButtonStyle}
          UrlCopyTooltip={UrlCopyTooltip}
        />
      )}
    </>
  );
}
